import { jsx, jsxs } from 'react/jsx-runtime';
import { createContext, forwardRef, useRef, useState, useMemo } from 'react';
import clsx from 'clsx';
import { accept } from '../utils/event.js';
import 'tabbable';
import { percentToCss } from '../utils/css.js';
import { deltaSnap, computePercent } from '../utils/math.js';
import { useDragV2 } from '../hooks/use-drag/use-drag-v2.js';
import { useValidatedContext } from '../hooks/use-validated-context.js';
import { isSnappingDisabled } from '../utils/snapping.js';
import { useSlider } from './slider-primitive.hooks.js';
import { clampSliderValue, formatterForStep, maybeSnapToHint, computePercentFromEvent, computeValue, snapToHintInPath, axisForOrientation } from './slider-primitive.utils.js';
import { hitbox, root } from './slider-primitive.css.js';

const SliderContext = createContext(null);
const SliderThumbContext = createContext(null);
/**
 * The root of the slider primitive component. This should wrap all other slider component children.
 *
 * The root represents the slider "track" that the `Thumb` slides along. The thumb slides along
 * _within_ the root - it stops when its edge reaches the matching end of the root. For a
 * horizontal slider those ends are left and right; for a vertical one, bottom (`min`) and
 * top (`max`).
 *
 * To achieve a slider where the thumb's middle lines up with the edge of the visible track, inset
 * the track by half the thumb's size along that axis.
 *
 * @see https://fpl.figma.design/components/primitives/slider/
 */
const Root = forwardRef(function Root(propsIn, ref) {
    const { className, style, children, min, max, step, bigStep, keyStep = step, precision = 'step', value: valueIn, valueText: valueTextIn, defaultValue, onChange, disabled = false, hints = null, loop = false, tabIndex = 0, orientation = 'horizontal', 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledby, 'aria-orientation': ariaOrientation = orientation, ...props } = useSlider(propsIn);
    const rootRef = useRef(null);
    const thumbRef = useRef(null);
    // Somewhat of a hack, but we keep track of whether we just dragged, and reset on keydown and
    // blur. This lets us hide the focus ring while dragging and the s or ctrl keys are used to
    // disable snapping. We use this to add a data attribute and combine with `:focus-visible` in css.
    const [justDragged, setJustDragged] = useState(false);
    // A live change is outstanding, waiting on the commit that ends the interaction
    const hasUncommittedChange = useRef(false);
    const value = clampSliderValue(valueIn, { min, max, step, precision });
    // Format announcements to the configured step even when dragging preserves finer precision.
    // Consumers can provide valueText when they need a different announcement.
    const formatter = useMemo(() => formatterForStep(step), [step]);
    const valueText = valueTextIn ?? formatter.format(value);
    const sliderContextValue = useMemo(() => ({ min, max, step, precision, orientation }), [min, max, step, precision, orientation]);
    const sliderThumbContextValue = useMemo(() => ({ value, thumbRef }), [value, thumbRef]);
    function updateValue(newValue, event) {
        if (!disabled && newValue !== value) {
            hasUncommittedChange.current = true;
            onChange?.(newValue, { event, commit: false });
        }
    }
    function commitValue(event, valueToCommit = value) {
        if (!disabled || hasUncommittedChange.current) {
            onChange?.(valueToCommit, { event, commit: true });
            hasUncommittedChange.current = false;
        }
    }
    const [dragging, getDragProps] = useDragV2({
        disabled,
        onDragStart() {
            setJustDragged(true);
            return value;
        },
        onDrag({ event: e }, startValue) {
            // Escape / pointercancel / going disabled mid-drag: commit the value the slider had before
            // the drag began.
            if (e.cancelled) {
                commitValue(e, startValue);
                return;
            }
            const ignoreHints = isSnappingDisabled(e);
            const percent = maybeSnapToHint(computePercentFromEvent(e, rootRef.current, thumbRef.current, orientation), ignoreHints ? null : hints, min, max);
            const newValue = computeValue(percent, { min, max, step, precision });
            if (e.commit) {
                commitValue(e);
            }
            else {
                updateValue(newValue, e);
            }
        },
    });
    function onKeyDown(e) {
        if (!dragging) {
            setJustDragged(false);
        }
        let newValue = value;
        let usedBigStep = false;
        const arrowKeyStep = e.shiftKey ? bigStep : e.altKey ? step : keyStep;
        switch (e.code) {
            case 'ArrowRight':
            case 'ArrowUp':
                usedBigStep = e.shiftKey;
                newValue = deltaSnap(newValue, arrowKeyStep, step);
                break;
            case 'ArrowLeft':
            case 'ArrowDown':
                usedBigStep = e.shiftKey;
                newValue = deltaSnap(newValue, -1 * arrowKeyStep, step);
                break;
            case 'PageUp':
                usedBigStep = true;
                newValue = deltaSnap(newValue, bigStep, step);
                break;
            case 'PageDown':
                usedBigStep = true;
                newValue = deltaSnap(newValue, -1 * bigStep, step);
                break;
            case 'Home':
                newValue = min;
                break;
            /* Voice control sends F4 when asked for the End key */
            case 'F4':
            case 'End':
                newValue = max;
                break;
            case 'Backspace':
            case 'Delete':
                accept(e);
                if (defaultValue != null) {
                    newValue = defaultValue;
                }
                break;
            case 'KeyS':
                // S is used to disable snapping
                accept(e);
                return;
            case 'Escape':
                accept(e);
                e.currentTarget.blur();
                return;
            default:
                return;
        }
        // prevents page scrolling for arrow keys
        accept(e);
        // Big-step movements (shift+arrow, PageUp/PageDown) should stop on hints they'd otherwise
        // jump over. Holding ctrl or S disables this, matching the drag-time snapping behavior.
        if (usedBigStep && !isSnappingDisabled(e)) {
            newValue = snapToHintInPath(value, newValue, hints, step);
        }
        // A move that runs off an end clamps to that end, so a big step lands on the boundary
        // rather than overshooting it. Once you're already sitting on the boundary, a fresh press
        // (not an auto-repeat) loops straight to the opposite end instead of jumping a step past
        // it. So crossing a looping slider's seam takes two presses -- hit the wall, then wrap --
        // and the wrap always lands exactly on the far end. This matches our other looping
        // components (menus, toolbars, tabs), which only wrap on a fresh press.
        if (newValue > max) {
            newValue = loop && !e.repeat && value === max ? min : max;
        }
        else if (newValue < min) {
            newValue = loop && !e.repeat && value === min ? max : min;
        }
        updateValue(newValue, e);
    }
    return (jsx(SliderContext.Provider, { value: sliderContextValue, children: jsx(SliderThumbContext.Provider, { value: sliderThumbContextValue, children: jsxs("div", { "data-fpl-component": "primitive", ...props, ref: rootRef, style: style, className: clsx(className, root), "data-fpl-orientation": orientation, "data-dragging": dragging || undefined, "data-focus-visible": !justDragged || undefined, children: [jsx("div", { ref: ref, ...getDragProps({ className: hitbox }), onKeyDown: onKeyDown, onBlur: (e) => {
                            setJustDragged(false);
                            if (hasUncommittedChange.current) {
                                commitValue(e);
                            }
                        }, role: "slider", tabIndex: tabIndex, "aria-valuemin": min, "aria-valuemax": max, "aria-valuenow": value, "aria-valuetext": valueText, "aria-disabled": disabled || undefined, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledby, "aria-orientation": ariaOrientation }), children] }) }) }));
});
Root.displayName = 'SliderPrimitive.Root';
/**
 * The primary control for the slider. This component should be nested within a `SliderPrimitive.Root`.
 */
function Thumb({ style, ...props }) {
    const { min, max, orientation } = useValidatedContext(SliderContext, 'Thumb', 'SliderPrimitive.Thumb');
    const { value, thumbRef } = useValidatedContext(SliderThumbContext, 'Thumb', 'SliderPrimitive.Thumb');
    const { minEdge, thumbTransform } = axisForOrientation(orientation);
    const offset = percentToCss(computePercent(value, min, max));
    return (jsx("div", { "data-fpl-component": "primitive", ...props, ref: thumbRef, style: { ...style, [minEdge]: offset, transform: thumbTransform } }));
}
Thumb.displayName = 'SliderPrimitive.Thumb';
/**
 * A helper component for adding a filled range to the slider. The given start and end values are
 * converted to percentages and used to set the inset properties for the slider's orientation -
 * `left`/`right` when horizontal, `bottom`/`top` when vertical.
 *
 * Start and end will automatically be reordered if start > end.
 */
function Range({ start, end, style, ...props }) {
    const { min, max, step, precision, orientation } = useValidatedContext(SliderContext, 'Range', 'SliderPrimitive.Range');
    start = clampSliderValue(start, { min, max, step, precision });
    end = clampSliderValue(end, { min, max, step, precision });
    if (start > end) {
        const tmp = end;
        end = start;
        start = tmp;
    }
    const { minEdge, maxEdge } = axisForOrientation(orientation);
    const startOffset = percentToCss(computePercent(start, min, max));
    const endOffset = percentToCss(1 - computePercent(end, min, max));
    return (jsx("div", { "data-fpl-component": "primitive", ...props, style: { ...style, [minEdge]: startOffset, [maxEdge]: endOffset } }));
}
Range.displayName = 'SliderPrimitive.Range';
/**
 * A helper component for adding a point to the slider. The given value is converted to a percentage
 * and used to set the inset property for the slider's orientation - `left` when horizontal,
 * `bottom` when vertical.
 */
function Point({ value, style, ...props }) {
    const { min, max, orientation } = useValidatedContext(SliderContext, 'Point', 'SliderPrimitive.Point');
    const { minEdge } = axisForOrientation(orientation);
    const offset = percentToCss(computePercent(value, min, max));
    return jsx("div", { "data-fpl-component": "primitive", ...props, style: { ...style, [minEdge]: offset } });
}
Point.displayName = 'SliderPrimitive.Point';

export { Point, Range, Root, Thumb };
//# sourceMappingURL=slider-primitive.js.map
